/* global axios */
import CacheEnabledApiClient from './CacheEnabledApiClient';

class Inboxes extends CacheEnabledApiClient {
  constructor() {
    super('inboxes', { accountScoped: true });
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    return 'inbox';
  }

  // The inbox payload carries `capabilities`, which the build that served it decides, so a
  // key fetched from a different request cannot vouch for these rows. The index sends the
  // key for the body it just built; without one, this response came from a build that does
  // not, and it is not cached at all.
  // eslint-disable-next-line class-methods-use-this
  get usesResponseBoundCacheKey() {
    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  cacheKeyFromResponse(response) {
    return response?.data?.cache_key ?? null;
  }

  // Keeps the locally cached inbox fresh on connection-status changes without bumping
  // the cache key (so it never triggers a full refetch). Silent if IDB is unavailable.
  async updateCachedProviderConnection(id, providerConnection) {
    try {
      await this.dataManager.initDb();
      await this.dataManager.update({
        modelName: this.cacheModelName,
        id,
        data: { provider_connection: providerConnection },
      });
    } catch {
      // Ignore
    }
  }

  async updateCachedAttendanceConnection(id, attendanceConnection) {
    try {
      await this.dataManager.initDb();
      await this.dataManager.update({
        modelName: this.cacheModelName,
        id,
        data: { attendance_connection: attendanceConnection },
      });
    } catch {
      // The in-memory inbox is already current when browser storage is unavailable.
    }
  }

  setupAttendanceConnection(id, data) {
    return axios.post(`${this.url}/${id}/setup_attendance_connection`, data);
  }

  attendanceConnectionStatus(id) {
    return axios.get(`${this.url}/${id}/attendance_connection_status`);
  }

  disconnectAttendanceConnection(id) {
    return axios.delete(`${this.url}/${id}/disconnect_attendance_connection`);
  }

  qrLinkCandidates(id) {
    return axios.get(`${this.url}/${id}/qr_link_candidates`);
  }

  linkQrChannel(id, qrChannelId) {
    return axios.post(`${this.url}/${id}/link_qr_channel`, {
      qr_channel_id: qrChannelId,
    });
  }

  unlinkQrChannel(id) {
    return axios.delete(`${this.url}/${id}/unlink_qr_channel`);
  }

  nativeInstances(id) {
    return axios.get(`${this.url}/${id}/native_instances`);
  }

  linkNativeInstance(id, providerId, instanceName) {
    return axios.post(`${this.url}/${id}/link_native_instance`, {
      provider_id: providerId,
      instance_name: instanceName,
    });
  }

  unlinkNativeInstance(id) {
    return axios.delete(`${this.url}/${id}/unlink_native_instance`);
  }

  async updateCachedGroupTransport(id, groupTransport) {
    try {
      await this.dataManager.initDb();
      await this.dataManager.update({
        modelName: this.cacheModelName,
        id,
        data: { group_transport: groupTransport },
      });
    } catch {
      // Ignore
    }
  }

  // Whether WhatsApp knows this phone, asked through the inbox's paired session.
  // Answers `{ jid, exists }`. Advisory: the server gives up early rather than hold the
  // request, since the caller only shows a hint.
  onWhatsapp(inboxId, phoneNumber) {
    return axios.post(`${this.url}/${inboxId}/on_whatsapp`, {
      phone_number: phoneNumber,
      advisory: true,
    });
  }

  getCampaigns(inboxId) {
    return axios.get(`${this.url}/${inboxId}/campaigns`);
  }

  deleteInboxAvatar(inboxId) {
    return axios.delete(`${this.url}/${inboxId}/avatar`);
  }

  getAgentBot(inboxId) {
    return axios.get(`${this.url}/${inboxId}/agent_bot`);
  }

  setAgentBot(inboxId, botId) {
    return axios.post(`${this.url}/${inboxId}/set_agent_bot`, {
      agent_bot: botId,
    });
  }

  getAgentBotObservers(inboxId) {
    return axios.get(`${this.url}/${inboxId}/agent_bot_observers`);
  }

  addAgentBotObserver(inboxId, botId) {
    return axios.post(`${this.url}/${inboxId}/agent_bot_observers`, {
      agent_bot: botId,
    });
  }

  removeAgentBotObserver(inboxId, botId) {
    return axios.delete(`${this.url}/${inboxId}/agent_bot_observers/${botId}`);
  }

  syncTemplates(inboxId) {
    return axios.post(`${this.url}/${inboxId}/sync_templates`);
  }

  getWhatsAppTemplates(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_templates`);
  }

  listYcloudPhoneNumbers(apiKey) {
    return axios.post(`${this.url}/ycloud/phone_numbers`, {
      api_key: apiKey,
    });
  }

  createWhatsAppTemplate(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/whatsapp_templates`, {
      template,
    });
  }

  updateWhatsAppTemplate(inboxId, name, language, template) {
    return axios.patch(`${this.url}/${inboxId}/whatsapp_templates/${name}`, {
      language,
      template,
    });
  }

  uploadWhatsAppTemplateMedia(inboxId, file) {
    const data = new FormData();
    data.append('file', file);
    return axios.post(
      `${this.url}/${inboxId}/whatsapp_templates/upload_header_media`,
      data,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  deleteWhatsAppTemplate(inboxId, name) {
    return axios.delete(`${this.url}/${inboxId}/whatsapp_templates/${name}`);
  }

  getWhatsAppPricingAnalytics(inboxId) {
    return axios.get(
      `${this.url}/${inboxId}/whatsapp_templates/pricing_analytics`
    );
  }

  getMessageTemplates(inboxId, params = {}, config = {}) {
    return axios.get(`${this.url}/${inboxId}/message_templates`, {
      ...config,
      params,
    });
  }

  updateWhatsappBusinessManagementToken(inboxId, businessManagementToken) {
    return axios.put(
      `${this.url}/${inboxId}/whatsapp_business_management_token`,
      {
        business_management_token: businessManagementToken,
      }
    );
  }

  createCSATTemplate(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/csat_template`, {
      template,
    });
  }

  getCSATTemplateStatus(inboxId) {
    return axios.get(`${this.url}/${inboxId}/csat_template`);
  }

  analyzeCSATTemplateUtility(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/csat_template/analyze`, {
      template,
    });
  }

  resetSecret(inboxId) {
    return axios.post(`${this.url}/${inboxId}/reset_secret`);
  }

  linkCSATTemplate(inboxId, template) {
    return axios.post(`${this.url}/${inboxId}/csat_template/link`, {
      template,
    });
  }

  getAvailableCSATTemplates(inboxId) {
    return axios.get(
      `${this.url}/${inboxId}/csat_template/available_templates`
    );
  }

  setupChannelProvider(inboxId) {
    return axios.post(`${this.url}/${inboxId}/setup_channel_provider`);
  }

  providerHealth(inboxId) {
    return axios.get(`${this.url}/${inboxId}/provider_health`);
  }

  requestPairingCode(inboxId) {
    return axios.post(`${this.url}/${inboxId}/request_pairing_code`);
  }

  disconnectChannelProvider(inboxId) {
    return axios.post(`${this.url}/${inboxId}/disconnect_channel_provider`);
  }

  setupGroupTransport(inboxId) {
    return axios.post(`${this.url}/${inboxId}/setup_group_transport`);
  }

  groupTransportStatus(inboxId) {
    return axios.get(`${this.url}/${inboxId}/group_transport_status`);
  }

  groupTransportPairingCode(inboxId) {
    return axios.post(`${this.url}/${inboxId}/group_transport_pairing_code`);
  }

  teardownGroupTransport(inboxId) {
    return axios.delete(`${this.url}/${inboxId}/teardown_group_transport`);
  }

  updateAgentSignature(inboxId, data) {
    return axios.patch(`${this.url}/${inboxId}/update_agent_signature`, data);
  }

  convertProvider(inboxId, { provider, providerConfig, whatsappProviderId }) {
    return axios.post(`${this.url}/${inboxId}/convert_provider`, {
      provider,
      provider_config: providerConfig,
      whatsapp_provider_id: whatsappProviderId,
    });
  }

  rotateHmacToken(inboxId) {
    return axios.post(`${this.url}/${inboxId}/rotate_hmac_token`);
  }

  getWhatsappCallingStatus(inboxId) {
    return axios.get(`${this.url}/${inboxId}/whatsapp_calling_status`);
  }

  enableWhatsappCalling(inboxId) {
    return axios.post(`${this.url}/${inboxId}/enable_whatsapp_calling`);
  }

  disableWhatsappCalling(inboxId) {
    return axios.post(`${this.url}/${inboxId}/disable_whatsapp_calling`);
  }

  setInboundCalls(inboxId, enabled) {
    return axios.post(`${this.url}/${inboxId}/set_inbound_calls`, {
      inbound_calls_enabled: enabled,
    });
  }

  // Camada de ligações universal (sessão de voz AstraCalls anexada à caixa)
  voiceSetup(inboxId) {
    return axios.post(`${this.url}/${inboxId}/voice_setup`);
  }

  voiceStatus(inboxId) {
    return axios.get(`${this.url}/${inboxId}/voice_status`);
  }

  voiceTeardown(inboxId) {
    return axios.delete(`${this.url}/${inboxId}/voice_teardown`);
  }

  setCallRecording(inboxId, { recordingEnabled, transcriptionEnabled }) {
    return axios.post(`${this.url}/${inboxId}/set_call_recording`, {
      recording_enabled: recordingEnabled,
      transcription_enabled: transcriptionEnabled,
    });
  }
}

export default new Inboxes();
